import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, mergeProps as _mergeProps, createBlock as _createBlock, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, withModifiers as _withModifiers } from "vue"

import { ref, onMounted } from 'vue';
import debounce from 'lodash.debounce';
import { useRoute } from 'vue-router';
import { remoteOptionsRegistry } from '@/lib/remoteOptions';
import { useLayoutField } from '@/composables/useLayoutField';
import { configRegistry } from '@/lib/configRegistry';


const __default__ = {
  name: 'Filters',
};

interface FilterOption {
  value: string;
  label: string;
  component: any; // Directly use Vuetify component
  placeholder?: string;
  prefix?: string;
  suffix?: string;
  numeric?: boolean;
  options?: any[];
  itemText?: string;
  itemValue?: string;
  fetchOptions?: keyof typeof remoteOptionsRegistry;
  range?: boolean;
  transform?:any;
}


export default /*@__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const route = useRoute();
const moduleName = (route.meta.moduleName ?? route.name) as string;
const filters = useLayoutField(moduleName, 'filters');

const filterConfig = ref<FilterOption[]>([]);
const form = ref<Record<string, any>>({});
const searchInputs = ref<Record<string, string>>({});
const loadingStates = ref<Record<string, boolean>>({});
const debouncedFetchers = ref<Record<string, (query: string) => void>>({});

const getProps = (filter: FilterOption) => {
  const key = filter.value;

  const props: Record<string, any> = {
    label: filter.label,
    placeholder: filter.placeholder ?? '',
    type: filter.numeric ? 'number' : 'text',
    clearable: true,
    density: 'compact',
    range:filter.range
  };

  if (filter.prefix) props.prefix = filter.prefix;
  if (filter.suffix) props.suffix = filter.suffix;

  // Static options
  if (filter.options) {
    props.items = filter.options;
    props['item-title'] = filter.itemText || 'text';
    props['item-value'] = filter.itemValue || 'value';
  }

  // Dynamic options (via fetchOptions)
  if (filter.fetchOptions) {
    const fetchFn = remoteOptionsRegistry[filter.fetchOptions];

    if (!fetchFn) {
      console.warn(`❌ No se encontró la función "${filter.fetchOptions}" en remoteOptionsRegistry`);
      return props;
    }

    // Default
    filter.options = filter.options || [];

    props.items = filter.options;
    props['item-title'] = filter.itemText || 'label';
    props['item-value'] = filter.itemValue || 'value';
    props.search = searchInputs.value[key] || '';
    props.loading = loadingStates.value[key] || false;

    // Debounced search
    if (!debouncedFetchers.value[key]) {
      debouncedFetchers.value[key] = debounce(async (val: string) => {
        if (!val || val.length < 2) {
          filter.options = [];
          return;
        }

        loadingStates.value[key] = true;

        try {
          const results = await fetchFn(val);
          filter.options = results;
          props.items = results;
        } catch (err) {
          console.error(`❌ Error buscando "${filter.value}" con "${val}"`, err);
        } finally {
          loadingStates.value[key] = false;
        }
      }, 500);
    }

    props['onUpdate:search'] = (val: string) => {
      searchInputs.value[key] = val;
      debouncedFetchers.value[key](val);
    };
  }

  return props;
};

onMounted(async () => {
  const loader = configRegistry[moduleName];

  if (!loader) {
    console.error(`[Filters] No se encontró config para: "${moduleName}"`);
    return;
  }

  try {
    const mod = await loader();
    filterConfig.value = mod.FILTERS || mod.filters || [];
  } catch (err) {
    console.error(`[Filters] Error cargando config "${moduleName}"`, err);
  }

  filterConfig.value.forEach((f) => {
    form.value[f.value] = filters.value?.[f.value] ?? null;
  });
});

const applyFilters = () => {
  const clean: Record<string, any> = {};

  filterConfig.value.forEach(filter => {
    const val = form.value[filter.value];

    if (val !== null && val !== undefined && val !== '') {
      // 👇 Si existe un transform, úsalo
      if (typeof filter?.transform === 'function') {
        const transformed = filter?.transform(val, form.value);

        // Si el transform retorna objeto (como {subject_id, subject_type}), mezcla los valores
        if (typeof transformed === 'object' && !Array.isArray(transformed)) {
          Object.assign(clean, transformed);
        } else {
          clean[filter.value] = transformed;
        }
      } else {
        clean[filter.value] = val;
      }
    }
  });

  filters.value = clean;
};


const resetFilters = () => {
  Object.keys(form.value).forEach(k => form.value[k] = null);
  filters.value = {};
};

return (_ctx: any,_cache: any) => {
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_form = _resolveComponent("v-form")!

  return (_openBlock(), _createBlock(_component_v_form, {
    onSubmit: _withModifiers(applyFilters, ["prevent"])
  }, {
    default: _withCtx(() => [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filterConfig.value, (filter) => {
        return (_openBlock(), _createElementBlock("div", {
          key: filter.value,
          class: "mb-4"
        }, [
          (_openBlock(), _createBlock(_resolveDynamicComponent(filter.component), _mergeProps({
            modelValue: form.value[filter.value],
            "onUpdate:modelValue": ($event: any) => ((form.value[filter.value]) = $event),
            ref_for: true
          }, getProps(filter)), null, 16, ["modelValue", "onUpdate:modelValue"]))
        ]))
      }), 128)),
      _createVNode(_component_v_btn, {
        type: "submit",
        color: "primary",
        class: "mr-2"
      }, {
        default: _withCtx(() => _cache[0] || (_cache[0] = [
          _createTextVNode("Aplicar filtros")
        ])),
        _: 1
      }),
      _createVNode(_component_v_btn, {
        variant: "text",
        color: "red",
        onClick: resetFilters
      }, {
        default: _withCtx(() => _cache[1] || (_cache[1] = [
          _createTextVNode("Limpiar")
        ])),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})